/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ChangeEvent } from 'react';

import { Autocomplete, SelectField, SelectOption, TextField } from '@coursera/cds-core';

import InternationalPhoneNumberField from 'bundles/expression-of-interest/components/i18n-phone-number-input/InternationalPhoneNumberField';
import formStyles from 'bundles/expression-of-interest/components/i18n-phone-number-input/styles/FormStyles';
import type { ValidPhone } from 'bundles/expression-of-interest/components/i18n-phone-number-input/types';

import _t from 'i18n!nls/expression-of-interest';

export type SurveyDropdownOption = { id: string; text: string };

export type SurveyFieldConfig =
  | { id: string; kind: 'text' | 'email'; label: string; placeholder?: string; fullWidth?: boolean }
  | {
      id: string;
      kind: 'dropdown';
      label: string;
      placeholder?: string;
      fullWidth?: boolean;
      searchable?: boolean;
      options: SurveyDropdownOption[];
    }
  | ({ id: string } & PhoneQuestionFieldProps);

type BaseProps = { fullWidth?: boolean };

type TextQuestionFieldProps = BaseProps & {
  kind: 'text' | 'email';
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
};

type DropdownQuestionFieldProps = BaseProps & {
  kind: 'dropdown';
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  options: SurveyDropdownOption[];
  onOpen?: () => void;
  // Lets the visitor type to filter the options instead of scrolling a list.
  searchable?: boolean;
  // Skips the browser's "Please fill out this field" check so the form can submit unanswered.
  optional?: boolean;
};

type PhoneQuestionFieldProps = BaseProps & {
  kind: 'phone';
  value?: ValidPhone;
  // True once the async prefill has landed; unlike `value`, it never flips back while the visitor types.
  hydrated?: boolean;
  onChange: (value?: ValidPhone) => void;
  // Reports which part of the phone field the visitor started using.
  onFocus?: (inputName: 'phone_number' | 'country_code') => void;
};

export type SurveyQuestionFieldProps = TextQuestionFieldProps | DropdownQuestionFieldProps | PhoneQuestionFieldProps;

const styles = {
  field: css`
    grid-column: span 1;
  `,
  fieldFullWidth: css`
    grid-column: span 2;
  `,
  // Fields use the placeholder as the label, so hide the phone widget's internal labels.
  phoneWidget: css`
    ${formStyles.hideChildLabel}
  `,
};

const SurveyQuestionField = (props: SurveyQuestionFieldProps) => {
  const fieldStyle = props.fullWidth ? styles.fieldFullWidth : styles.field;

  if (props.kind === 'phone') {
    const { onChange, value, hydrated, onFocus } = props;
    return (
      <div css={fieldStyle} data-testid="survey-question-field-phone">
        <div css={styles.phoneWidget}>
          <InternationalPhoneNumberField
            // The widget only seeds its state from `phone` on mount, so remount once when prefill lands.
            key={hydrated ? 'prefilled' : 'empty'}
            compact
            errorDisplayStyle="overlay"
            phone={value}
            // Blank is incomplete, not invalid; the parent gates Continue on a real number.
            isEmptyValid
            submissionError={false}
            onPhoneNumberChange={onChange}
            onTextFieldFocus={() => onFocus?.('phone_number')}
            onCountryCodeOpen={() => onFocus?.('country_code')}
          />
        </div>
      </div>
    );
  }

  if (props.kind === 'dropdown') {
    const { label, placeholder, value, onChange, onOpen, options, searchable, optional } = props;
    const onChangeWrapper = (e: ChangeEvent<{ value: unknown }>) => {
      const selectedOption = options.find((option) => option.id === e.target.value);
      if (selectedOption != null) {
        onChange(selectedOption.id);
      }
    };

    if (searchable) {
      return (
        <Autocomplete
          data-testid="survey-question-field-autocomplete"
          css={fieldStyle}
          aria-label={label}
          placeholder={placeholder}
          selectedKey={value === '' ? undefined : value}
          popoverTrigger="focus"
          fullWidth
          onOpen={onOpen}
          onSelectionChange={(key) => {
            if (key != null) {
              onChange(String(key));
            }
          }}
          renderNotFoundMessage={() => _t('No results found')}
        >
          {options.map((option) => (
            <Autocomplete.Option key={option.id}>{option.text}</Autocomplete.Option>
          ))}
        </Autocomplete>
      );
    }

    return (
      <SelectField
        data-testid="survey-question-field-dropdown"
        css={fieldStyle}
        value={value}
        onChange={onChangeWrapper}
        onOpen={onOpen}
        fullWidth
        aria-label={label}
        placeholder={placeholder}
        optional={optional}
      >
        {options.map((option) => (
          <SelectOption key={option.id} value={option.id}>
            {option.text}
          </SelectOption>
        ))}
      </SelectField>
    );
  }

  const { label, placeholder, value, onChange, onFocus, kind } = props;
  return (
    <TextField
      data-testid="survey-question-field-text"
      css={fieldStyle}
      type={kind === 'email' ? 'email' : 'text'}
      fullWidth
      aria-label={label}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
    />
  );
};

export default SurveyQuestionField;
